import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Gauge } from 'lucide-react';
import type { MeterRow } from '@/utils/canonicalTransform';

interface MeterTableProps {
  data: MeterRow[];
  onEdit?: (field: string, value: any, rowIndex: number) => void;
}

export function MeterTable({ data, onEdit }: MeterTableProps) {
  const formatValue = (value: any, type: 'date' | 'number' | 'text' = 'text') => {
    if (value === null || value === undefined || value === '') return '';
    
    if (type === 'date' && typeof value === 'string') {
      try {
        return new Date(value).toISOString().split('T')[0];
      } catch {
        return value;
      }
    }
    
    if (type === 'number' && typeof value === 'string') {
      const num = parseFloat(value);
      return isNaN(num) ? value : num.toString();
    }
    
    return value.toString();
  };

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Gauge className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p>No meter data available</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-orange-600" />
          <h3 className="text-sm font-medium text-gray-900">Meter Readings</h3>
          <Badge variant="secondary" className="text-xs">
            {data.length} {data.length === 1 ? 'meter' : 'meters'}
          </Badge>
        </div>
      </div>
      <div className="overflow-auto max-h-[350px] force-scrollbar">
        <div className="table-wrap">
          <Table className="w-full">
          <TableHeader className="sticky top-0 bg-gray-50 z-10">
            <TableRow>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Identifier
              </TableHead>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Meter Number
              </TableHead>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Multiplier
              </TableHead>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Previous Reading
              </TableHead>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Current Reading
              </TableHead>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Quantity
              </TableHead>
              <TableHead className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Unit
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="divide-y divide-gray-200">
            {data.map((meter, index) => (
              <TableRow key={index} className="hover:bg-gray-50">
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatValue(meter.identifier)}
                </TableCell>
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatValue(meter.meterNumber)}
                </TableCell>
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatValue(meter.multiplier, 'number')}
                </TableCell>
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatValue(meter.previousReading, 'number')}
                </TableCell>
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatValue(meter.currentReading, 'number')}
                </TableCell>
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatValue(meter.quantity, 'number')}
                </TableCell>
                <TableCell className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatValue(meter.unit)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
